/**
 * Data layer for `soc-2-iso-27001-attested`. Thin typed fetch wrappers over /api/v1/soc-2-iso-27001-attested.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface Soc2Iso27001AttestedRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListSoc2Iso27001AttestedResponse {
	count: number;
	items: Soc2Iso27001AttestedRow[];
}

export interface CreateSoc2Iso27001AttestedBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/soc-2-iso-27001-attested";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListSoc2Iso27001AttestedResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListSoc2Iso27001AttestedResponse>(res);
}

export async function create(body: CreateSoc2Iso27001AttestedBody): Promise<Soc2Iso27001AttestedRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<Soc2Iso27001AttestedRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const soc2Iso27001AttestedQueryKeys = {
	all: ["soc-2-iso-27001-attested"] as const,
	list: () => [...soc2Iso27001AttestedQueryKeys.all] as const,
	detail: (id: string) => [...soc2Iso27001AttestedQueryKeys.all, id] as const,
};
